import {
    ChangeDetectionStrategy,
    Component,
    inject,
    ViewEncapsulation,
    ViewChild,
    computed,
    AfterViewInit,
} from '@angular/core';
import { IOnboardingEditItem, IOnboardingSection } from '../../fi-onboarding';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { ObjectTypes } from '@fi-shared/types';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { IContentLanguage } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
// import { FiTwoColumnsContainerComponent } from '@fi-shared/fi-two-columns-container/fi-two-columns-container.component';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';
import { FiOnboardingBuilderStoreService } from '../../fi-onboarding-builder-store.service';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { IMediaBankFileReference } from '@fi-app/shared/media-bank';
import { FiThreeColumnsComponent } from '@fi-app/shared/fi-three-columns-container/fi-three-columns-container.component';

@Component({
    selector: 'fi-onboarding-editor-tab',
    templateUrl: './fi-onboarding-editor-tab.component.html',
    styleUrls: ['./fi-onboarding-editor-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-onboarding-editor-tab' },
    providers: [{ provide: FiComponentsContainer, useExisting: FiOnboardingEditorTabComponent }],
    standalone: false,
})
export class FiOnboardingEditorTabComponent extends FiComponentsContainer implements AfterViewInit {
    private fiContentLanguageSharedService = inject(FiContentLanguageSharedService);
    private fiOnboardingBuilderStoreService = inject(FiOnboardingBuilderStoreService);

    protected onboarding = computed<IOnboardingEditItem>(() =>
        this.fiOnboardingBuilderStoreService.currentOnboarding(),
    );
    protected selectedLanguageId = computed<FiApplicationLanguageType>(() =>
        this.fiOnboardingBuilderStoreService.selectedLanguageId(),
    );

    // @ViewChild('fiTwoColumnsContainer') fiTwoColumnsContainer: FiTwoColumnsContainerComponent;
    @ViewChild('fiThreeColumnsContainer') fiThreeColumnsContainer: FiThreeColumnsComponent;
    protected readonly ObjectTypes = ObjectTypes;
    protected availableContentLanguages: IContentLanguage[] = this.fiContentLanguageSharedService.getContentLanguages();

    public ngAfterViewInit(): void {
        this.scrollToHighlightedStep();
    }

    private scrollToHighlightedStep(): void {
        const highlightedStepElementId = this.fiOnboardingBuilderStoreService.highlightedStepElementId();
        if (!highlightedStepElementId) {
            return;
        }

        const container = this.fiThreeColumnsContainer.columnsContainer.nativeElement;
        const elementToScrollTo = container.querySelector(`#${highlightedStepElementId}`);
        if (isNullOrUndefined(elementToScrollTo)) {
            console.warn(`'Element to scroll to (stepElementId: ${highlightedStepElementId}) not found'`);
            return;
        }
        const containerRect = container.getBoundingClientRect();
        const targetRect = elementToScrollTo.getBoundingClientRect();

        container.scrollTo({
            top: container.scrollTop + (targetRect.top - containerRect.top),
            behavior: 'smooth',
        });
    }

    protected onSelectedLanguageIdChange(updatedLanguageId: FiApplicationLanguageType): void {
        this.fiOnboardingBuilderStoreService.setSelectedLanguageId(updatedLanguageId);
    }

    protected onOnboardingDescriptionsChange(updatedDescriptions: IContentLanguageLabelModel[]): void {
        this.fiOnboardingBuilderStoreService.updateCurrentOnboarding((current) => ({
            ...current,
            Descriptions: updatedDescriptions,
        }));
    }
    protected onOnboardingMediaBankFileReferencesChange(mediaBankFileReferences: IMediaBankFileReference[]): void {
        this.fiOnboardingBuilderStoreService.updateCurrentOnboarding((current) => ({
            ...current,
            MediaBankFileReferences: mediaBankFileReferences,
        }));
    }
    protected onOnboardingNamesChange(updatedNames: IContentLanguageLabelModel[]): void {
        this.fiOnboardingBuilderStoreService.updateCurrentOnboarding((current) => ({
            ...current,
            Names: updatedNames,
        }));
    }
    protected onOnboardingSectionsChange(updatedSections: IOnboardingSection[]): void {
        this.fiOnboardingBuilderStoreService.updateCurrentOnboarding((current) => ({
            ...current,
            Sections: updatedSections,
        }));
    }
}
